import { useState, useEffect } from "react";
import axios from "axios";
import { DashboardLayout, adminNavItems } from "@/components/DashboardLayout";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Star,
  Link as LinkIcon,
  Copy,
  Check,
  X,
  Plus,
  Trash2,
  Clock,
  CheckCircle,
  XCircle,
  AlertCircle,
  Mail,
  ExternalLink,
  Download,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminTestimonials() {
  const { token } = useAuth();
  const [invites, setInvites] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);

  // Form
  const [customerName, setCustomerName] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [note, setNote] = useState("");
  const [creating, setCreating] = useState(false);

  const authHeaders = { headers: { Authorization: `Bearer ${token}` } };

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [invRes, testRes] = await Promise.all([
        axios.get(`${API}/api/testimonials/admin/invites`, authHeaders),
        axios.get(`${API}/api/testimonials/admin/list`, authHeaders),
      ]);
      setInvites(invRes.data || []);
      setTestimonials(testRes.data || []);
    } catch (err) {
      toast.error("Kunne ikke laste data");
    } finally {
      setLoading(false);
    }
  };

  const createInvite = async () => {
    setCreating(true);
    try {
      const res = await axios.post(
        `${API}/api/testimonials/admin/invite`,
        {
          customer_name: customerName || null,
          customer_email: customerEmail || null,
          note: note || null,
        },
        authHeaders
      );
      setShowCreateDialog(false);
      setCustomerName("");
      setCustomerEmail("");
      setNote("");
      setShowLinkDialog(res.data);
      fetchAll();
      toast.success("Invitasjon opprettet!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke opprette invitasjon");
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (url, token) => {
    navigator.clipboard.writeText(url);
    setCopiedToken(token);
    toast.success("Lenke kopiert!");
    setTimeout(() => setCopiedToken(null), 2000);
  };

  const revokeInvite = async (token) => {
    if (!window.confirm("Slett denne invitasjonen?")) return;
    try {
      await axios.delete(`${API}/api/testimonials/admin/invite/${token}`, authHeaders);
      toast.success("Invitasjon slettet");
      fetchAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke slette");
    }
  };

  const approveTestimonial = async (id) => {
    try {
      await axios.post(`${API}/api/testimonials/admin/${id}/approve`, {}, authHeaders);
      toast.success("Anmeldelse godkjent og publisert");
      fetchAll();
    } catch (err) {
      toast.error("Kunne ikke godkjenne");
    }
  };

  const rejectTestimonial = async (id) => {
    if (!window.confirm("Avvis denne anmeldelsen?")) return;
    try {
      await axios.post(`${API}/api/testimonials/admin/${id}/reject`, {}, authHeaders);
      toast.success("Anmeldelse avvist");
      fetchAll();
    } catch (err) {
      toast.error("Kunne ikke avvise");
    }
  };

  const deleteTestimonial = async (id) => {
    if (!window.confirm("Slett anmeldelsen permanent?")) return;
    try {
      await axios.delete(`${API}/api/testimonials/admin/${id}`, authHeaders);
      toast.success("Slettet");
      fetchAll();
    } catch (err) {
      toast.error("Kunne ikke slette");
    }
  };

  const pending = testimonials.filter((t) => t.status === "pending");
  const approved = testimonials.filter((t) => t.status === "approved");
  const rejected = testimonials.filter((t) => t.status === "rejected");

  const isExpired = (iso) => new Date(iso) < new Date();

  return (
    <DashboardLayout navItems={adminNavItems} title="Anmeldelser & Invitasjoner">
      <div className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard label="Venter" value={pending.length} icon={<Clock className="w-5 h-5" />} color="orange" />
          <StatCard label="Godkjent" value={approved.length} icon={<CheckCircle className="w-5 h-5" />} color="green" />
          <StatCard label="Avvist" value={rejected.length} icon={<XCircle className="w-5 h-5" />} color="red" />
          <StatCard label="Aktive lenker" value={invites.filter(i => !i.used && !isExpired(i.expires_at)).length} icon={<LinkIcon className="w-5 h-5" />} color="cyan" />
        </div>

        <div className="flex justify-between items-center">
          <h2 className="text-2xl font-['Unbounded'] font-bold text-white">Anmeldelser</h2>
          <div className="flex gap-2">
            <Button
              onClick={async () => {
                try {
                  const res = await axios.get(`${API}/api/testimonials/admin/export`, {
                    ...authHeaders,
                    responseType: "blob",
                  });
                  const url = window.URL.createObjectURL(new Blob([res.data]));
                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute("download", `testimonials_${new Date().toISOString().slice(0, 10)}.csv`);
                  document.body.appendChild(link);
                  link.click();
                  link.remove();
                  toast.success("CSV lastet ned");
                } catch (err) {
                  toast.error("Kunne ikke eksportere");
                }
              }}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="export-csv-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Eksporter CSV
            </Button>
            <Button
              onClick={() => setShowCreateDialog(true)}
              className="bg-[#F7931A] text-white hover:bg-[#F7931A]/80"
              data-testid="create-invite-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Ny invitasjonslenke
            </Button>
          </div>
        </div>

        <Tabs defaultValue="pending">
          <TabsList className="bg-[#0A0A0A] border border-white/10">
            <TabsTrigger value="pending" data-testid="tab-pending">Venter ({pending.length})</TabsTrigger>
            <TabsTrigger value="approved" data-testid="tab-approved">Godkjent ({approved.length})</TabsTrigger>
            <TabsTrigger value="rejected" data-testid="tab-rejected">Avvist ({rejected.length})</TabsTrigger>
            <TabsTrigger value="invites" data-testid="tab-invites">Lenker ({invites.length})</TabsTrigger>
          </TabsList>

          <TabsContent value="pending" className="mt-4">
            <TestimonialsList
              items={pending}
              onApprove={approveTestimonial}
              onReject={rejectTestimonial}
              onDelete={deleteTestimonial}
              showActions
            />
          </TabsContent>

          <TabsContent value="approved" className="mt-4">
            <TestimonialsList
              items={approved}
              onDelete={deleteTestimonial}
              onReject={rejectTestimonial}
            />
          </TabsContent>

          <TabsContent value="rejected" className="mt-4">
            <TestimonialsList
              items={rejected}
              onApprove={approveTestimonial}
              onDelete={deleteTestimonial}
            />
          </TabsContent>

          <TabsContent value="invites" className="mt-4">
            {invites.length === 0 ? (
              <EmptyCard text="Ingen invitasjoner ennå" />
            ) : (
              <div className="space-y-3">
                {invites.map((inv) => {
                  const expired = isExpired(inv.expires_at);
                  const url = `${window.location.origin}/anmeldelse/${inv.token}`;
                  return (
                    <Card key={inv.invite_id} className="bg-[#0A0A0A] border-white/10">
                      <CardContent className="p-4 flex items-center justify-between gap-4 flex-wrap">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-white font-medium">
                              {inv.customer_name || "Anonym"}
                            </span>
                            {inv.customer_email && (
                              <span className="text-[#94A3B8] text-sm flex items-center gap-1">
                                <Mail className="w-3 h-3" />
                                {inv.customer_email}
                              </span>
                            )}
                            {inv.used ? (
                              <Badge className="bg-green-500/20 text-green-400 border border-green-500/30">
                                Brukt
                              </Badge>
                            ) : expired ? (
                              <Badge className="bg-red-500/20 text-red-400 border border-red-500/30">
                                Utløpt
                              </Badge>
                            ) : (
                              <Badge className="bg-[#23F7DD]/20 text-[#23F7DD] border border-[#23F7DD]/30">
                                Aktiv
                              </Badge>
                            )}
                          </div>
                          {inv.note && (
                            <p className="text-[#94A3B8] text-sm mb-1">📝 {inv.note}</p>
                          )}
                          <p className="text-[#64748B] text-xs">
                            Opprettet: {new Date(inv.created_at).toLocaleDateString("no-NO")} | Utløper:{" "}
                            {new Date(inv.expires_at).toLocaleDateString("no-NO")}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {!inv.used && !expired && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => copyLink(url, inv.token)}
                              className="border-white/20 text-white"
                              data-testid={`copy-link-${inv.token}`}
                            >
                              {copiedToken === inv.token ? (
                                <Check className="w-4 h-4 mr-1" />
                              ) : (
                                <Copy className="w-4 h-4 mr-1" />
                              )}
                              Kopier
                            </Button>
                          )}
                          {!inv.used && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => revokeInvite(inv.token)}
                              className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>

      {/* Create Invite Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle>Ny invitasjonslenke</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Kundens navn (valgfritt)</label>
              <Input
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Ola Nordmann"
                className="bg-black/40 border-white/20"
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">E-post (valgfritt)</label>
              <Input
                type="email"
                value={customerEmail}
                onChange={(e) => setCustomerEmail(e.target.value)}
                placeholder="kunde@example.com"
                className="bg-black/40 border-white/20"
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <label className="text-sm text-[#94A3B8] mb-1 block">Internt notat (valgfritt)</label>
              <Textarea
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Prosjekt: Tak-renovering Frogner"
                className="bg-black/40 border-white/20 min-h-[80px]"
                data-testid="invite-note-input"
              />
            </div>
            <div className="flex items-start gap-2 p-3 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-lg text-sm">
              <AlertCircle className="w-4 h-4 text-[#F7931A] flex-shrink-0 mt-0.5" />
              <p className="text-[#FFCD00]">
                Lenken er gyldig i <strong>7 dager</strong> og kan brukes <strong>én gang</strong>.
              </p>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)} className="border-white/20 text-white">
                Avbryt
              </Button>
              <Button
                onClick={createInvite}
                disabled={creating}
                className="bg-[#F7931A] text-white"
                data-testid="confirm-create-invite-btn"
              >
                {creating ? "Oppretter..." : "Opprett lenke"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Show generated link */}
      <Dialog open={!!showLinkDialog} onOpenChange={(open) => !open && setShowLinkDialog(null)}>
        <DialogContent className="bg-[#0A0A0A] border-[#23F7DD]/30 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-[#23F7DD]" />
              Lenke opprettet!
            </DialogTitle>
          </DialogHeader>
          {showLinkDialog && (
            <div className="space-y-4">
              <p className="text-[#94A3B8] text-sm">
                Send denne lenken til kunden. Lenken er gyldig i 7 dager.
              </p>
              <div className="p-3 bg-black/40 border border-white/10 rounded-lg break-all font-mono text-sm text-[#23F7DD]">
                {`${window.location.origin}/anmeldelse/${showLinkDialog.token}`}
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => copyLink(`${window.location.origin}/anmeldelse/${showLinkDialog.token}`, showLinkDialog.token)}
                  className="flex-1 bg-[#F7931A] text-white"
                  data-testid="copy-new-link-btn"
                >
                  <Copy className="w-4 h-4 mr-2" />
                  Kopier lenke
                </Button>
                <a
                  href={`${window.location.origin}/anmeldelse/${showLinkDialog.token}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <Button variant="outline" className="border-white/20 text-white">
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                </a>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}

// ============= Subcomponents =============

const StatCard = ({ label, value, icon, color }) => {
  const colors = {
    orange: "bg-[#F7931A]/20 text-[#F7931A]",
    green: "bg-green-500/20 text-green-400",
    red: "bg-red-500/20 text-red-400",
    cyan: "bg-[#23F7DD]/20 text-[#23F7DD]",
  };
  return (
    <Card className="bg-[#0A0A0A] border-white/10">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-10 h-10 ${colors[color]} rounded-lg flex items-center justify-center`}>
          {icon}
        </div>
        <div>
          <p className="text-xl font-bold text-white">{value}</p>
          <p className="text-[#94A3B8] text-xs">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const EmptyCard = ({ text }) => (
  <Card className="bg-[#0A0A0A] border-white/10">
    <CardContent className="p-12 text-center">
      <p className="text-[#94A3B8]">{text}</p>
    </CardContent>
  </Card>
);

const TestimonialsList = ({ items, onApprove, onReject, onDelete, showActions }) => {
  if (items.length === 0) return <EmptyCard text="Ingen anmeldelser her" />;
  return (
    <div className="space-y-3">
      {items.map((t) => (
        <Card key={t.testimonial_id} className="bg-[#0A0A0A] border-white/10" data-testid={`admin-testimonial-${t.testimonial_id}`}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 mb-2">
                  <span className="text-white font-semibold">{t.name}</span>
                  <div className="flex items-center gap-0.5">
                    {[1, 2, 3, 4, 5].map((n) => (
                      <Star
                        key={n}
                        className={`w-4 h-4 ${
                          n <= t.rating ? "fill-yellow-400 text-yellow-400" : "text-[#475569]"
                        }`}
                      />
                    ))}
                  </div>
                  <span className="text-[#64748B] text-xs">
                    {new Date(t.created_at).toLocaleDateString("no-NO")}
                  </span>
                </div>
                <p className="text-[#CBD5E1] text-sm leading-relaxed">"{t.comment}"</p>
              </div>
              <div className="flex items-center gap-2">
                {showActions && onApprove && (
                  <Button
                    size="sm"
                    onClick={() => onApprove(t.testimonial_id)}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                    data-testid={`approve-${t.testimonial_id}`}
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Godkjenn
                  </Button>
                )}
                {showActions && onReject && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReject(t.testimonial_id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                    data-testid={`reject-${t.testimonial_id}`}
                  >
                    <X className="w-4 h-4 mr-1" />
                    Avvis
                  </Button>
                )}
                {!showActions && onApprove && t.status === "rejected" && (
                  <Button
                    size="sm"
                    onClick={() => onApprove(t.testimonial_id)}
                    className="bg-green-500/20 text-green-400 hover:bg-green-500/30 border border-green-500/30"
                  >
                    <Check className="w-4 h-4 mr-1" />
                    Reaktiver
                  </Button>
                )}
                {!showActions && onReject && t.status === "approved" && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onReject(t.testimonial_id)}
                    className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10"
                  >
                    Skjul
                  </Button>
                )}
                {onDelete && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onDelete(t.testimonial_id)}
                    className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
